export default {
  // Upload state getters
  loading: state => state.loading,
  isUploading: state => state.loading,
  uploadProgress: state => state.uploadProgress,
  currentUpload: state => state.currentUpload,
  uploadedCreatives: state => state.uploadedCreatives,

  // Form getters
  uploadForm: state => state.uploadForm,
  isFormValid: state => {
    const form = state.uploadForm
    const validation = state.fileValidation

    // Check if advertiser ID is selected
    if (!form.advertiserId) {
      return false
    }

    // Check file validation
    if (!validation.isValid) {
      return false
    }

    // Check required fields based on upload method
    switch (form.uploadMethod) {
      case 'UPLOAD_BY_FILE':
        return !!form.file
      case 'UPLOAD_BY_URL':
        return !!form.url
      case 'UPLOAD_BY_FILE_ID':
        return !!form.fileId
      case 'UPLOAD_BY_VIDEO_ID':
        return !!form.videoId && form.type === 'video'
      default:
        return false
    }
  },

  // File validation getters
  fileValidation: state => state.fileValidation,
  fileValidationErrors: state => state.fileValidation.errors,
  isFileValid: state => state.fileValidation.isValid,

  // Pre-review form validation
  isPreReviewFormValid: state => {
    const form = state.uploadForm
    return !!(form.adText && form.landingPageUrl && form.locationCodes.length > 0)
  },

  // Creatives list getters
  creativesList: state => state.creativesList,
  creativesTotal: state => state.creativesTotal,
  creativesPage: state => state.creativesPage,
  creativesPageSize: state => state.creativesPageSize,
  creativesFilters: state => state.creativesFilters,
  creativesSort: state => state.creativesSort,

  // Get creative by ID
  getCreativeById: state => id => state.creativesList.find(creative => creative.id === id)
           || state.uploadedCreatives.find(creative => creative.id === id),

  // Get creatives by status
  getCreativesByStatus: state => status => state.creativesList.filter(creative => creative.status === status),

  // Get creatives by pre-review status
  getCreativesByPreReviewStatus: state => status => state.creativesList.filter(creative => creative.preReviewStatus === status),

  // Get creatives by type
  getCreativesByType: state => type => state.creativesList.filter(creative => creative.type === type),

  // Get filtered creatives count
  filteredCreativesCount: state => {
    if (!state.creativesFilters.search && !state.creativesFilters.status
        && !state.creativesFilters.type && !state.creativesFilters.preReviewStatus) {
      return state.creativesTotal
    }
    return state.creativesList.length
  },

  // Check if filters are active
  hasActiveFilters: state => !!(state.creativesFilters.search || state.creativesFilters.status
                               || state.creativesFilters.type || state.creativesFilters.preReviewStatus
                               || state.creativesFilters.dateRange),

  // Get pagination info
  paginationInfo: state => ({
    currentPage: state.creativesPage,
    pageSize: state.creativesPageSize,
    total: state.creativesTotal,
    totalPages: Math.ceil(state.creativesTotal / state.creativesPageSize),
    hasNextPage: state.creativesPage < Math.ceil(state.creativesTotal / state.creativesPageSize),
    hasPrevPage: state.creativesPage > 1,
  }),

  // Get statistics
  creativesStats: state => {
    const stats = {
      total: state.creativesTotal,
      uploaded: 0,
      processing: 0,
      completed: 0,
      failed: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
      unavailable: 0,
    }

    state.creativesList.forEach(creative => {
      if (creative.status) {
        stats[creative.status] = (stats[creative.status] || 0) + 1
      }
      if (creative.preReviewStatus) {
        stats[creative.preReviewStatus] = (stats[creative.preReviewStatus] || 0) + 1
      }
    })

    return stats
  },

  // Upload method options
  videoUploadMethods: () => [
    { value: 'UPLOAD_BY_FILE', text: 'Upload File' },
    { value: 'UPLOAD_BY_URL', text: 'Upload by URL' },
    { value: 'UPLOAD_BY_FILE_ID', text: 'Upload by File ID' },
    { value: 'UPLOAD_BY_VIDEO_ID', text: 'Upload by Video ID' },
  ],

  imageUploadMethods: () => [
    { value: 'UPLOAD_BY_FILE', text: 'Upload File' },
    { value: 'UPLOAD_BY_URL', text: 'Upload by URL' },
    { value: 'UPLOAD_BY_FILE_ID', text: 'Upload by File ID' },
  ],

  // Get available upload methods based on type
  availableUploadMethods: (state, getters) => (state.uploadForm.type === 'video' ? getters.videoUploadMethods : getters.imageUploadMethods),

  // Check if upload is in progress
  hasActiveUpload: state => state.loading && state.uploadProgress > 0 && state.uploadProgress < 100,

  // Get upload status text
  uploadStatusText: state => {
    if (!state.loading && state.uploadProgress === 0) return 'Ready to upload'
    if (state.loading && state.uploadProgress < 100) return `Uploading... ${state.uploadProgress}%`
    if (state.uploadProgress === 100) return 'Upload completed'
    return 'Ready to upload'
  },
}
